import {React, useState, useEffect, useRef} from 'react'
import { getAuthKey } from '../config/secrets';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_SITE_KEY =
  process.env.NODE_ENV === 'production'
    ? '6Ldg_jsrAAAAADnrP3ecBkwVHOaqZXmfWHBxdoFU'
    : '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const Footer = ({data, ipAddress}) => {
    const formConfig = data.formOptions?.footer || {
        fields: {
            firstName: { enabled: true },
            lastName: { enabled: true },
            email: { enabled: true },
            zipCode: { enabled: true },
            city: { enabled: true },
            message: { enabled: true }
        },
        submitText: 'Contact Us'
    };

    const enabledFields = Object.entries(formConfig.fields).filter(([_, v]) => v.enabled).map(([k]) => k);

    const initialFormData = Object.fromEntries(enabledFields.map(f => [f, '']));

    const [formData, setFormData] = useState(initialFormData);
    const [submitted, setSubmitted] = useState(false);
    const [showForm, setShowForm] = useState(true);
    const [authKey, setAuthKey] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState('');

    const formRef = useRef(null);
    const thankYouRef = useRef(null);

    useEffect(() => {
        setFormData(Object.fromEntries(enabledFields.map(f => [f, ''])));
    }, [data.domainName]);

    useEffect(() => {
        // Get auth key on component mount
        setAuthKey(getAuthKey());
    }, []);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        
        // Direct DOM manipulation for immediate UI feedback
        if (formRef.current && thankYouRef.current) {
            formRef.current.style.display = 'none';
            thankYouRef.current.style.display = 'block';
        }
        
        // Use normalized domain name for consistency between www and non-www
        const submissionDomain = window.normalizedDomainName || data.domainName;
        
        const submissionData = {
            authKey,
            ipAddress: ipAddress,
            sourceUrl: window.location.href,
            domainName: submissionDomain,
            recaptchaToken,
        };
        enabledFields.forEach(f => {
            submissionData[f] = formData[f];
        });
        
        try {
            // Update state variables
            setShowForm(false);
            setSubmitted(true);
            
            // Reset form and recaptcha
            setFormData(Object.fromEntries(enabledFields.map(f => [f, ''])));
            setRecaptchaToken('');
            
            // Send the request in the background
            sendFormData(submissionData);
        } catch (error) {
            console.error('An error occurred while preparing the form:', error);
            alert('An error occurred. Please try again later.');
            
            // Revert UI changes on error
            if (formRef.current && thankYouRef.current) {
                formRef.current.style.display = 'block';
                thankYouRef.current.style.display = 'none';
            }
            
            setShowForm(true);
            setSubmitted(false);
            setSubmitting(false);
        }
    };

    // Separate function to handle the actual API call
    const sendFormData = async (data) => {
        try {
            const response = await fetch('https://vi76zjzy3k.execute-api.us-west-2.amazonaws.com/production/leads', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                console.error('Form submission failed', errorData);
                // If needed, you could still set submitted back to false here
                // but we're keeping the thank you message showing regardless
            }
        } catch (error) {
            console.error('An error occurred during API call:', error);
            // API errors won't affect the thank you message display
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <section id="contact">
                <h2 id="dev-snippet-title" className="text-center">{data.footerContactHeader || 'Contact Us'}</h2>
                <div className="container mr_top_10">
                    <div className="row">
                        <div className="col-lg-7 col-md-6 col-sm-6">
                            
                            {/* Thank you message - initially hidden */}
                            <div ref={thankYouRef} style={{ display: !showForm ? 'block' : 'none' }}>
                                <p className="thank-you-message">{data.thankYouMessage || 'Thank you for your submission!'}</p>
                            </div>
                            
                            {/* Form - initially visible */}
                            <div ref={formRef} style={{ display: showForm ? 'block' : 'none' }}>
                                <form onSubmit={handleSubmit}>
                                    {formConfig.fields.firstName?.enabled && (
                                        <div className="form-group">
                                            <div className="input-group">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="firstName"
                                                    placeholder="Enter First Name"
                                                    value={formData.firstName || ''}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <span className="input-group-addon">
                                                    <i className="glyphicon glyphicon-user form-control-feedback"></i>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {formConfig.fields.lastName?.enabled && (
                                        <div className="form-group">
                                            <div className="input-group">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="lastName"
                                                    placeholder="Enter Last Name"
                                                    value={formData.lastName || ''}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <span className="input-group-addon">
                                                    <i className="glyphicon glyphicon-user form-control-feedback"></i>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {formConfig.fields.email?.enabled && (
                                        <div className="form-group">
                                            <div className="input-group">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    name="email"
                                                    placeholder="Enter Email"
                                                    value={formData.email || ''}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <span className="input-group-addon">
                                                    <i className="glyphicon glyphicon-send form-control-feedback"></i>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {formConfig.fields.zipCode?.enabled && (
                                        <div className="form-group">
                                            <div className="input-group">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="zipCode"
                                                    placeholder="Enter Zip Code"
                                                    value={formData.zipCode || ''}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <span className="input-group-addon">
                                                    <i className="glyphicon glyphicon-pushpin form-control-feedback"></i>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {formConfig.fields.city?.enabled && (
                                        <div className="form-group">
                                            <div className="input-group">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="city"
                                                    placeholder="Enter City"
                                                    value={formData.city || ''}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <span className="input-group-addon">
                                                    <i className="glyphicon glyphicon-map-marker form-control-feedback"></i>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {formConfig.fields.message?.enabled && (
                                        <div className="form-group">
                                            <div className="input-group">
                                                <textarea
                                                    name="message"
                                                    className="form-control"
                                                    rows="5"
                                                    placeholder="Type Message Text"
                                                    value={formData.message || ''}
                                                    onChange={handleInputChange}
                                                    required
                                                ></textarea>
                                                <span className="input-group-addon">
                                                    <i className="glyphicon glyphicon-envelope form-control-feedback"></i>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="recaptcha-center">
                                        <ReCAPTCHA
                                            sitekey={RECAPTCHA_SITE_KEY}
                                            onChange={token => setRecaptchaToken(token)}
                                            style={{ marginBottom: 16 }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <button type="submit" className="btn btn-default quote_btn" disabled={submitting || !recaptchaToken}>
                                            {submitting ? (
                                                <span className="spinner" style={{marginRight: 8, display: 'inline-block', verticalAlign: 'middle', width: 18, height: 18}}>
                                                    <svg width="18" height="18" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="90,150" strokeLinecap="round"><animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/></circle></svg>
                                                </span>
                                            ) : null}
                                            {submitting ? 'Submitting...' : (formConfig.submitText || 'Submit')}
                                        </button>
                                    </div>
                                    <div className="footer-terms" style={{ fontSize: '0.85em', marginTop: '10px', color: '#666' }}>
                                        By signing up you are agreeing to our
                                        <a href="/terms" style={{ margin: '0 4px' }} target="_blank" rel="noopener noreferrer">Terms</a>
                                        &
                                        <a href="/privacy-policy" style={{ margin: '0 4px' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                                    </div>
                                </form>
                            </div>
                        </div>
                        {data.showFooterAddress !== false && (
                            <div className="col-lg-4 col-md-5 col-sm-6 col-md-offset-1">
                                <address>
                                    <p className="address_icon"><i className="icon-home"></i></p>
                                    <p className="lead">{data.brandname} <br /> {data.address}</p>
                                </address>
                            </div>
                        )}
                    </div>
                </div>
            </section>
            <footer>
                <div className="container">
                    <p>&copy; {new Date().getFullYear()} {data.brandname}. All Rights Reserved.</p>
                    <ul className="list-inline">
                        <li>
                            <a href="/privacy-policy" className="footer-link">Privacy</a>
                        </li>
                        <li>
                            <a href="/terms" className="footer-link">Terms</a>
                        </li>
                        <li>
                            <a href={data.unsubscribeUrl || "/unsubscribe"} className="footer-link">Unsubscribe</a>
                        </li>
                    </ul>
                </div>
            </footer>
        </>
    )
}

export default Footer

